import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import NumberScroller from "./components/NumberScroller";
import ReasonDisplay, { getReasonData } from "./components/ReasonDisplay";
import MobileApp from "./components/MobileApp";
import useSound from "use-sound";
import tickSoundFile from "./assets/tick.mp3";
import { Toaster } from "react-hot-toast";
import StarField from "./components/StarField";
import SpaceJourneyClean from "./components/SpaceJourneyClean";
import { motion, AnimatePresence } from "framer-motion";
import QRCodeDialog from "./components/QRCodeDialog";
import PerformanceMonitor from "./components/PerformanceMonitor";
import { Analytics } from "@vercel/analytics/react";
import {
  isDevelopment,
  shouldShowPerformanceMonitor,
} from "./utils/environment";
// Browser detection utility
const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return {
    isArc: userAgent.includes("arc"),
    isSafari: userAgent.includes("safari") && !userAgent.includes("chrome"),
    isChrome: userAgent.includes("chrome"),
    isMobile: window.innerWidth <= 768,
  };
};

// Enhanced mobile browser UI hiding function
const triggerMobileBrowserUIHide = () => {
  const browser = detectBrowser();

  if (!browser.isMobile) return;

  const scrollStrategies = () => {
    // Strategy 1: Quick scroll down and back up
    setTimeout(() => {
      window.scrollTo({ top: 1, behavior: "auto" });
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      }, 50);
    }, 100);

    // Strategy 2: Arc browser specific - more aggressive approach
    if (browser.isArc) {
      setTimeout(() => {
        window.scrollTo({ top: 10, behavior: "smooth" });
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 200);
      }, 300);
    }

    // Strategy 3: Backup scroll with different timing
    setTimeout(() => {
      window.scrollTo({ top: 1, behavior: "smooth" });
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }, 600);
  };

  scrollStrategies();
};

// Flag to ensure artwork is only displayed once
let artworkDisplayed = false;

// Space-themed console artwork function
const displaySpaceArtwork = () => {
  // Prevent multiple displays
  if (artworkDisplayed) return;
  artworkDisplayed = true;
  // Define color styles for different parts
  const styles = {
    spacecraft: "color: #C0C0C0; font-weight: bold;",
    spacecraftDark: "color: #808080; font-weight: bold;",
    spacecraftLight: "color: #E5E5E5; font-weight: bold;",
    fire: "color: #FF4500; font-weight: bold;",
    fireCore: "color: #FFD700; font-weight: bold;",
    fireOuter: "color: #FF0000; font-weight: bold;",
    stars: "color: #FFFFFF; font-weight: bold;",
    plumBrand: "color: #8A2BE2; font-weight: bold; font-size: 16px;",
    text: "color: #00FF00; font-weight: bold; font-size: 14px;",
    url: "color: #00BFFF; font-weight: bold; text-decoration: underline; font-size: 14px;",
    border: "color: #FFFF00; font-weight: bold;",
    cockpit: "color: #87CEEB; font-weight: bold;",
  };

  console.log(
    "%c                  ✦    ✧       ★      ✦    ✧       ★",
    styles.stars
  );
  console.log(
    "%c            ✧         ★    ✦       ✧         ★    ✦",
    styles.stars
  );
  console.log(
    "%c       ★         ✦              ★         ✦         ",
    styles.stars
  );
  console.log("%c                           ╭─────╮", styles.spacecraftLight);
  console.log("%c                          ╱       ╲", styles.spacecraftLight);
  console.log("%c                         ╱    ⚡    ╲", styles.cockpit);
  console.log(
    "%c                        ╱  ┌─────┐  ╲",
    styles.spacecraftLight
  );
  console.log("%c                       ╱   │ ◉ ◉ │   ╲", styles.cockpit);
  console.log("%c                      ╱    │  ◦  │    ╲", styles.cockpit);
  console.log(
    "%c                     ╱     └─────┘     ╲",
    styles.spacecraftLight
  );
  console.log("%c                    ╱                   ╲", styles.spacecraft);
  console.log("%c                   ╱      ████████      ╲", styles.spacecraft);
  console.log(
    "%c                  ╱      ██      ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c                 ╱      ██        ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c                ╱      ██          ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c               ╱      ██            ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c              ╱      ██   %cP L U M%c    ██      ╲",
    styles.spacecraft,
    styles.plumBrand,
    styles.spacecraft
  );
  console.log(
    "%c             ╱      ██                ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c            ╱      ██                  ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c           ╱      ██                    ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c          ╱      ██ ████████████████████ ██      ╲",
    styles.spacecraft
  );
  console.log(
    "%c         ╱      ██ ██                  ██ ██      ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c        ╱       ██ ██                  ██ ██       ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c       ╱        ██ ██                  ██ ██        ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c      ╱         ██ ██                  ██ ██         ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c     ╱          ██ ██████████████████████ ██          ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c    ╱▲▲▲▲▲▲▲▲▲▲██ ████████    ████████ ██▲▲▲▲▲▲▲▲▲▲╲",
    styles.spacecraftDark
  );
  console.log(
    "%c   ╱            ████████          ████████            ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c  ╱              ██████            ██████              ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c ╱                ████              ████                ╲",
    styles.spacecraftDark
  );
  console.log(
    "%c╱▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲████▲▲▲▲▲▲▲▲▲▲▲▲████▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲╲",
    styles.spacecraftDark
  );
  console.log(
    "%c                   ██                ██",
    styles.spacecraftDark
  );
  console.log(
    "%c                  %c░██░%c              %c░██░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c                 %c░░██░░%c            %c░░██░░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c                %c░░░██░░░%c          %c░░░██░░░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c               %c░░░░██░░░░%c        %c░░░░██░░░░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c              %c░░░░░██░░░░░%c      %c░░░░░██░░░░░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c             %c░░░░░░██░░░░░░%c    %c░░░░░░██░░░░░░",
    styles.spacecraftDark,
    styles.fire,
    styles.spacecraftDark,
    styles.fire
  );
  console.log(
    "%c            %c███████████████%c  %c███████████████",
    styles.spacecraftDark,
    styles.fireCore,
    styles.spacecraftDark,
    styles.fireCore
  );
  console.log(
    "%c           %c████▓▓▓▓▓▓▓▓████%c  %c████▓▓▓▓▓▓▓▓████",
    styles.spacecraftDark,
    styles.fireCore,
    styles.spacecraftDark,
    styles.fireCore
  );
  console.log(
    "%c          %c███▓▓▓▓▓▓▓▓▓▓▓███%c  %c███▓▓▓▓▓▓▓▓▓▓▓███",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c         %c██▓▓▓▓▓▓▓▓▓▓▓▓▓▓██%c  %c██▓▓▓▓▓▓▓▓▓▓▓▓▓▓██",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c        %c█▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓█%c    %c█▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓█",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c       %c▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓%c      %c▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c      %c▓▓▓░░▓▓▓▓▓▓▓▓░░▓▓▓%c        %c▓▓▓░░▓▓▓▓▓▓▓▓░░▓▓▓",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c     %c▓▓░░  ░░▓▓▓▓░░  ░░▓▓%c      %c▓▓░░  ░░▓▓▓▓░░  ░░▓▓",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c    %c▓░░      ░░▓▓░░      ░░▓%c  %c▓░░      ░░▓▓░░      ░░▓",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log(
    "%c   %c░░          ░░░░          ░░%c %c░          ░░░░          ░░",
    styles.spacecraftDark,
    styles.fireOuter,
    styles.spacecraftDark,
    styles.fireOuter
  );
  console.log("");
  console.log(
    "%c   ╔═══════════════════════════════════════════════════════════╗",
    styles.border
  );
  console.log(
    "%c   ║                                                           ║",
    styles.border
  );
  console.log(
    "%c   ║     %cLike building cool projects like this?%c                ║",
    styles.border,
    styles.text,
    styles.border
  );
  console.log(
    "%c   ║                                                           ║",
    styles.border
  );
  console.log(
    "%c   ║     🚀 %cSign up at plumhq.com/careers%c                      ║",
    styles.border,
    styles.url,
    styles.border
  );
  console.log(
    "%c   ║                                                           ║",
    styles.border
  );
  console.log(
    "%c   ║     %cJoin us in the healthcare cosmos! 🌌%c                  ║",
    styles.border,
    styles.text,
    styles.border
  );
  console.log(
    "%c   ║                                                           ║",
    styles.border
  );
  console.log(
    "%c   ╚═══════════════════════════════════════════════════════════╝",
    styles.border
  );
  console.log("");
  console.log(
    "%c      ★         ✦              ★         ✦         ✧",
    styles.stars
  );
  console.log(
    "%c            ✧         ★    ✦       ✧         ★    ",
    styles.stars
  );
  console.log(
    "%c                  ✦    ✧       ★      ✦    ✧    ",
    styles.stars
  );
};

// Add UFO component before AppContainer
const UFO = styled(motion.div)`
  position: absolute;
  top: 0%;
  right: -5%;
  width: 180px;
  height: 120px;
  background: url("/ufo-background.png") no-repeat center center;
  background-size: contain;
  z-index: -1;
  transition: z-index 0s;
`;

// Update AppContainer to remove the UFO from ::after
const AppContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  margin: 0 auto;
  gap: 2.5rem;
  position: relative;
  overflow: hidden;
  padding: 0 2.5vw;
  z-index: 2;

  /* Responsive widths for different viewport sizes */
  width: 100%;

  @media (min-width: 1536px) {
    max-width: 1700px;
  }
`;

const NumberScrollerWrapper = styled.div`
  flex: 0 0 30%; /* Take exactly 30% of the width */
  max-width: 30%;
  height: 100%;
  overflow: visible;
`;

// Update the BackgroundWrapper to have a higher z-index
const BackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1; // Change from -1 to 0 to make stars visible
`;

// New: ReasonSection for text and image side by side
const ReasonSection = styled.div`
  flex: 1; /* Take remaining 70% of the space */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  gap: 0; /* Remove gap to ensure exact 50-50 split */

  /* Child elements get equal distribution */
  > * {
    flex: 1 1 50%;
    max-width: 50%;
  }

  @media (max-width: 1100px) {
    gap: 1.2rem;
    > * {
      flex: 1 1 calc(50% - 0.6rem); /* Adjust for gap */
      max-width: calc(50% - 0.6rem);
    }
  }
  @media (max-width: 900px) {
    gap: 0.5rem;
    > * {
      flex: 1 1 calc(50% - 0.25rem); /* Adjust for gap */
      max-width: calc(50% - 0.25rem);
    }
  }
`;

const PhoneImage = styled(motion.div).withConfig({
  shouldForwardProp: prop => prop !== "imageUrl",
})`
  width: 280px;
  height: 560px;
  background-image: url("${props =>
    encodeURI(props.imageUrl || "/phone-mockup.png")}");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  @media (max-width: 1100px) {
    width: 180px;
    height: 360px;
  }
  @media (max-width: 900px) {
    width: 120px;
    height: 240px;
  }
`;

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentReason, setCurrentReason] = useState(0);
  const [cameraZ, setCameraZ] = useState(0); // Camera state for synchronization
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useSound(tickSoundFile, { volume: 0.5 });
  const scrollerRef = useRef(null);
  const isFirstRender = useRef(true);
  const [isQRDialogOpen, setIsQRDialogOpen] = useState(false);
  const [performanceMode, setPerformanceMode] = useState(false);
  const [fps, setFps] = useState(60);

  // Add this effect to ensure we start with reason 0
  useEffect(() => {
    // Force the initial reason to be 0 on component mount
    setCurrentReason(0);
  }, []);

  // Performance monitoring and auto-optimization
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let fpsCheck;

    const checkPerformance = () => {
      frameCount++;
      const currentTime = performance.now();

      if (currentTime - lastTime >= 1000) {
        const currentFPS = Math.round(
          (frameCount * 1000) / (currentTime - lastTime)
        );
        setFps(currentFPS);

        // Auto-enable performance mode if FPS drops below 30
        if (currentFPS < 30 && !performanceMode) {
          setPerformanceMode(true);
          console.log(
            "🚀 Performance mode enabled automatically (FPS:",
            currentFPS,
            ")"
          );
        }

        frameCount = 0;
        lastTime = currentTime;
      }

      fpsCheck = requestAnimationFrame(checkPerformance);
    };

    fpsCheck = requestAnimationFrame(checkPerformance);

    return () => {
      if (fpsCheck) cancelAnimationFrame(fpsCheck);
    };
  }, [performanceMode]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Display console artwork once on desktop only
  useEffect(() => {
    // Only show console art on desktop devices, run once on mount
    if (window.innerWidth > 768 && typeof window !== "undefined") {
      displaySpaceArtwork();

      // Show development mode indicator
      if (isDevelopment()) {
        console.log(
          "%c🚀 Development Mode Active - Performance Monitor Available",
          "color: #00ff00; font-weight: bold; font-size: 12px;"
        );
        console.log(
          "%cPress Ctrl+P to toggle performance monitor or click 'Show Perf' button",
          "color: #888; font-size: 10px;"
        );
      }
    }
  }, []); // Empty dependency array ensures it runs only once

  const handleReasonChange = (number, isShuffling = false) => {
    // Don't play sound on the very first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setCurrentReason(number);
      return;
    }

    // Always play the sound when the number changes
    // play();

    // Update the current reason
    setCurrentReason(number);
  };

  // Handler for camera position updates from HyperspaceLoader
  const handleCameraUpdate = newCameraZ => {
    setCameraZ(newCameraZ);
  };

  // Handler for animation completion
  const handleAnimationComplete = () => {
    // Trigger mobile browser UI hiding before showing MobileApp
    if (isMobile) {
      triggerMobileBrowserUIHide();
    }

    setIsLoading(false);
    setCameraZ(0); // Reset camera
  };

  // Get the current reason data to access the image
  const currentReasonData =
    currentReason > 0 ? getReasonData(currentReason) : null;
  const currentImage = currentReasonData
    ? currentReasonData.image
    : "/42 reasons images/Manage Multiple Insurance Policies.png";

  // Monitor currentReason state changes
  useEffect(() => {
    // State change monitoring removed for performance
  }, [currentReason]);

  return (
    <>
      <Analytics />
      {shouldShowPerformanceMonitor() && (
        <PerformanceMonitor
          enabled={true}
          onPerformanceToggle={setPerformanceMode}
          performanceMode={performanceMode}
          fps={fps}
        />
      )}
      {isLoading ? (
        <SpaceJourneyClean
          onComplete={handleAnimationComplete}
          onCameraUpdate={handleCameraUpdate}
        />
      ) : isMobile ? (
        <MobileApp
          currentReason={currentReason}
          onReasonChange={handleReasonChange}
          cameraZ={cameraZ}
          currentReasonData={currentReasonData}
        />
      ) : (
        <>
          <BackgroundWrapper>
            <StarField
              cameraZ={cameraZ}
              intensity={performanceMode ? 1.0 : 2.5}
              performanceMode={performanceMode}
            />
          </BackgroundWrapper>
          <AppContainer>
            {!performanceMode && (
              <UFO
                initial={{
                  top: "0%",
                  right: "-5%",
                  rotate: 0,
                  scale: 1,
                }}
                animate={{
                  rotate: [0, 10, -10, 10, -10, 10, -10, 10, 0, 0],
                  top: [
                    "0%",
                    "20%",
                    "50%",
                    "80%",
                    "50%",
                    "20%",
                    "50%",
                    "20%",
                    "0%",
                    "0%",
                  ],
                  right: [
                    "-5%",
                    "20%",
                    "50%",
                    "20%",
                    "-5%",
                    "-20%",
                    "0%",
                    "10%",
                    "-5%",
                    "-5%",
                  ],
                  scale: [1, 0.9, 0.8, 0.9, 1, 0.9, 0.8, 0.9, 1, 1],
                  zIndex: [2, 2, -1, -1, -1, 2, 2, 2, 2, 2],
                }}
                transition={{
                  duration: 12,
                  times: [0, 0.1, 0.3, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 1],
                  ease: "easeInOut",
                  repeat: 0,
                }}
              />
            )}

            <NumberScrollerWrapper>
              <NumberScroller
                ref={scrollerRef}
                currentNumber={currentReason}
                onNumberChange={handleReasonChange}
                onOpenQRDialog={() => setIsQRDialogOpen(true)}
              />
            </NumberScrollerWrapper>

            <ReasonSection>
              <ReasonDisplay
                reasonNumber={currentReason}
                onOpenQRDialog={() => setIsQRDialogOpen(true)}
              />
              <AnimatePresence mode="wait">
                <PhoneImage
                  key={`phone-${currentReason}-${currentImage}`}
                  imageUrl={currentImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeIn" }}
                />
              </AnimatePresence>
            </ReasonSection>
            <Toaster />
          </AppContainer>
          <QRCodeDialog
            isOpen={isQRDialogOpen}
            onClose={() => setIsQRDialogOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default App;
